// src/pages/Admin/ExpensesAdmin.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { HeaderPage, TableRecurringExpenses } from '../../components/Admin';

import { DialogBasic, ModalBasic } from '../../components/common';
import { useSnackbar } from 'notistack';
import { TableExpenses } from '../../components/Admin/Finance/TableExpenses';
import { AddEditExpenseForm } from '../../components/Admin/Finance/forms/AddEditExpenseForm';

//import { DialogBasic, ModalBasic } from "././components/common";




import ExpensesDateFilterBar from "../../components/Admin/Finance/Filters/ExpensesDateFilterBar";
import { useExpenses } from "../../hook";

// ✅ nuevo hook dedicado a gastos
//import { useExpenses } from "././hook/useExpenses";

// ✅ barra visual de fecha (tipo screenshot)
//import ExpensesDateFilterBar from "././components/Admin/Finance/filters/ExpensesDateFilterBar";

/* =========================
   Helpers de fechas (ISO)
   ========================= */
const toISODate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
const endOfMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0);
const addMonths = (date, delta) => new Date(date.getFullYear(), date.getMonth() + delta, 1);

const today = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const getRangeFromPreset = ({ preset, month, customFrom, customTo }) => {
  // Devuelve { date_after, date_before } o null si no hay rango válido
  if (preset === "CUSTOM") {
    if (!customFrom || !customTo) return null;
    return { date_after: toISODate(customFrom), date_before: toISODate(customTo) };
  }

  if (preset === "THIS_MONTH") {
    return { date_after: toISODate(startOfMonth(month)), date_before: toISODate(endOfMonth(month)) };
  }

  if (preset === "LAST_MONTH") {
    const prev = addMonths(month, -1);
    return { date_after: toISODate(startOfMonth(prev)), date_before: toISODate(endOfMonth(prev)) };
  }

  if (preset === "LAST_6_MONTHS") {
    const from = startOfMonth(addMonths(month, -5));
    const to = endOfMonth(month);
    return { date_after: toISODate(from), date_before: toISODate(to) };
  }

  if (preset === "LAST_YEAR") {
    const from = startOfMonth(addMonths(month, -11));
    const to = endOfMonth(month);
    return { date_after: toISODate(from), date_before: toISODate(to) };
  }

  return null;
};

export function ExpensesAdmin() {
  const {
    isReady,
    expenses,
    expensesCount,
    loadingExpenses,
    getExpenses,
    createExpense,
    updateExpense,
    removeExpense,
    expenseCategories,
    getExpenseCategories,
  } = useExpenses();

  const { enqueueSnackbar } = useSnackbar();

  const [openDialog, setOpenDialog] = useState(false);
  const [idItem, setIdItem] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [titleModal, setTitleModal] = useState(null);
  const [contentModal, setContentModal] = useState(null);

  // =========================
  // Filtros fecha (barra)
  // =========================
  const [preset, setPreset] = useState("THIS_MONTH");
  const [month, setMonth] = useState(startOfMonth(today()));
  const [customFrom, setCustomFrom] = useState(null);
  const [customTo, setCustomTo] = useState(null);

  // =========================
  // Filtro categoría (simple)
  // =========================
  const [categoryId, setCategoryId] = useState("");

  // =========================
  // Paginación server-side
  // =========================
  const [page, setPage] = useState(0); // MUI 0-based
  const [pageSize, setPageSize] = useState(20);

  const openCloseModal = () => setShowModal((prev) => !prev);

  // ===========
  // Cargar cats
  // ===========
  useEffect(() => {
    if (!isReady) return;
    getExpenseCategories({ ordering: "name" }).catch(() => {});
  }, [isReady, getExpenseCategories]);

  // =========================
  // Construir rango y params
  // =========================
  const dateRange = useMemo(() => {
    return getRangeFromPreset({ preset, month, customFrom, customTo });
  }, [preset, month, customFrom, customTo]);

  const queryParams = useMemo(() => {
    const params = {
      ordering: "-date",
      page: page + 1, // DRF 1-based
      page_size: pageSize,
    };

    // Para performance: SIEMPRE intentamos enviar rango
    if (dateRange?.date_after) params.date_after = dateRange.date_after;
    if (dateRange?.date_before) params.date_before = dateRange.date_before;

    if (categoryId) params.category = categoryId;

    return params;
  }, [page, pageSize, dateRange, categoryId]);

  // =========================
  // Fetch (cada cambio)
  // =========================
  useEffect(() => {
    if (!isReady) return;

    // Si es CUSTOM y no hay rango aún, no consultamos
    if (preset === "CUSTOM" && !dateRange) return;

    getExpenses(queryParams).catch(() => {});
  }, [isReady, preset, dateRange, queryParams, getExpenses]);

  // =========================
  // Handlers
  // =========================
  const handleDateBarChange = useCallback(({ preset: nextPreset, month: nextMonth }) => {
    setPreset(nextPreset);
    setMonth(startOfMonth(nextMonth));

    // reset pagination al cambiar filtros
    setPage(0);

    // limpiar custom si no aplica
    if (nextPreset !== "CUSTOM") {
      setCustomFrom(null);
      setCustomTo(null);
    }
  }, []);

  const addExpense = () => {
    setTitleModal("Nuevo Gasto");
    setContentModal(
      <AddEditExpenseForm
        categories={expenseCategories}
        onClose={openCloseModal}
        onSuccess={() => {
          // refrescar manteniendo filtros/paginación actuales
          getExpenses(queryParams).catch(() => {});
          enqueueSnackbar("Gasto creado", { variant: "success" });
        }}
        onSubmit={createExpense}
      />
    );
    openCloseModal();
  };

  const editExpenseHandler = (data) => {
    setTitleModal("Editar Gasto");
    setContentModal(
      <AddEditExpenseForm
        categories={expenseCategories}
        expense={data}
        onClose={openCloseModal}
        onSuccess={() => {
          getExpenses(queryParams).catch(() => {});
          enqueueSnackbar("Gasto actualizado", { variant: "success" });
        }}
        onSubmit={(payload) => updateExpense(data.id, payload)}
      />
    );
    openCloseModal();
  };

  const onDeleteExpense = (data) => {
    setIdItem(data.id);
    setOpenDialog(true);
  };

  const handleCloseDialog = async (confirmed) => {
    if (!confirmed) {
      setOpenDialog(false);
      return;
    }

    try {
      await removeExpense(idItem);
      enqueueSnackbar("Gasto eliminado", { variant: "success" });

      // Si borras el último item de la última página, opcionalmente podrías retroceder 1 página.
      // Aquí mantenemos simple:
      await getExpenses(queryParams);
    } catch (err) {
      enqueueSnackbar("Error eliminando gasto", { variant: "error" });
    } finally {
      setOpenDialog(false);
    }
  };


  const duplicateExpenseHandler = (row) => {
  setTitleModal("Duplicar Gasto");

  // Clonamos el objeto sin id para crear uno nuevo
  const clone = { ...row };
  delete clone.id;

  setContentModal(
    <AddEditExpenseForm
      categories={expenseCategories}
      expense={clone} // reutiliza el form para “precargar”
      onClose={openCloseModal}
      onSuccess={() => {
        getExpenses(queryParams).catch(() => {});
        enqueueSnackbar("Gasto duplicado", { variant: "success" });
      }}
      onSubmit={createExpense}
    />
  );

  openCloseModal();
};


  // =========================
  // UI: filtro custom (simple)
  // =========================
  const customFromValue = customFrom ? toISODate(customFrom) : "";
  const customToValue = customTo ? toISODate(customTo) : "";

  return (
    <>
      <HeaderPage title="Gastos" btnClick={addExpense} />

      {/* Barra visual tipo screenshot */}
      <div style={{ marginBottom: 12 }}>
        <ExpensesDateFilterBar month={month} preset={preset} onChange={handleDateBarChange} />
      </div>

      {/* Fila de filtros extra (por ahora: categoría + custom date) */}
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
        {/* Categoría */}
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>Categoría:</span>
          <select
            value={categoryId}
            onChange={(e) => {
              setCategoryId(e.target.value);
              setPage(0);
            }}
          >
            <option value="">Todas</option>
            {expenseCategories?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        {/* Custom dates */}
        {preset === "CUSTOM" && (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Desde:</span>
            <input
              type="date"
              value={customFromValue}
              onChange={(e) => {
                setCustomFrom(e.target.value ? new Date(e.target.value) : null);
                setPage(0);
              }}
            />

            <span style={{ fontSize: 13, fontWeight: 600 }}>Hasta:</span>
            <input
              type="date"
              value={customToValue}
              onChange={(e) => {
                setCustomTo(e.target.value ? new Date(e.target.value) : null);
                setPage(0);
              }}
            />

            <button
              onClick={() => {
                // El useEffect disparará el fetch cuando dateRange sea válido
                setPage(0);
              }}
              disabled={!customFrom || !customTo}
            >
              Aplicar
            </button>
          </div>
        )}
      </div>

      {loadingExpenses ? (
        <p>Cargando...</p>
      ) : (
        <div className="container_menupolis row">
          <div className="mp-lg-12">
            <TableExpenses
              expenses={expenses}
              editExpense={editExpenseHandler}
              onDeleteExpense={onDeleteExpense}
              onDuplicateExpense={duplicateExpenseHandler}
              count={expensesCount}
              page={page}
              rowsPerPage={pageSize}
              onPageChange={setPage}
              onRowsPerPageChange={(n) => {
                setPageSize(n);
                setPage(0);
              }}
            />
          </div>

        </div>
      )}

      <ModalBasic show={showModal} onClose={openCloseModal} title={titleModal} size={"medium"}>
        {contentModal}
      </ModalBasic>

      <DialogBasic open={openDialog} onClose={handleCloseDialog} />
    </>
  );
}
